import { ButtonBrand } from 'components/ButtonBrand';
import { TopIssueCard } from 'components/Cards/TopIssueCard';
import { Link } from 'components/Link';
import Spacing from 'components/Spacing';
import { Body, BodySmall, Eyebrow } from 'components/Typography';
import theme from 'config/theme';
import { formatNumberAsCurrency, getPathWithRef, getUrlParams } from 'helpers/utils';
import { FC, useEffect, useState } from 'react';
import { getRecipients, getRecipientsAPI } from 'store/recipients/actions';
import { IRecipient, RecipientsRequestType } from 'store/recipients/types';
import { IPage } from 'store/donatePage/types';
import {
  DonateAgainCardContainer,
  DonateAgainCardLearnMore,
  Line,
  Recipient,
  RecipientsContainer,
} from './styled';
import { CandidateType } from 'components/Slate/Candidates/Candidates';
import { getCandidates, getCandidatesWithNewAmounts } from 'helpers/donate';
import { clearDonationSelections, setDonationSelections } from 'services/storage';
import { getDonatePage } from 'store/donatePage/actions';
import { useAppDispatch } from 'helpers/hooks';
import { trackDonateAgainClicked } from 'helpers/mixpanelEvents';
import { getPayments } from 'store/payments/actions';
import { useLocation } from 'react-router-dom';

interface Props {
  donationAmount: number;
  handleNewDonationSelections: () => void;
  issue: IPage;
  userId: string;
}

export const DEDonateAgainCard: FC<Props> = ({
  donationAmount,
  handleNewDonationSelections,
  issue,
  userId,
}) => {
  const dispatch = useAppDispatch();
  const [recipients, setRecipients] = useState([] as IRecipient[]);
  const [candidates, setCandidates] = useState<CandidateType[]>([]);
  const [donationAmountAdjusted, setDonationAmountAdjusted] = useState(donationAmount);
  const { urlPartner } = getUrlParams();
  const location = useLocation();

  const requestLocationPath = location.pathname;

  useEffect(() => {
    setDonationAmountAdjusted(donationAmount);
  }, [donationAmount]);

  useEffect(() => {
    if (!recipients.length) {
      return;
    }
    let amount = donationAmount;
    if (recipients.length > donationAmount) {
      amount = recipients.length;
      setDonationAmountAdjusted(amount);
    }
    setCandidates(getCandidatesWithNewAmounts(amount, getCandidates(recipients)));
  }, [recipients, donationAmount]);

  useEffect(() => {
    if (recipients.length) {
      return;
    }
    getRecipientsAPI({
      amount: 200,
      recipient_ids: issue.recipient_ids || [],
      request_type: RecipientsRequestType.checkout,
      page_query_str: urlPartner,
      request_location_path: requestLocationPath,
    }).then(response => {
      setRecipients(response.data.recipients);
    });
  }, [issue.query_str, recipients.length]);

  const handleDonate = () => {
    if (!candidates.length) {
      return;
    }
    dispatch(getDonatePage({ urlPartner: issue.query_str }));
    dispatch(
      getRecipients({
        amount: 100,
        recipient_ids: issue.recipient_ids || [],
        request_location_path: requestLocationPath,
        request_type: RecipientsRequestType.checkout,
      })
    );
    if (userId) {
      dispatch(getPayments());
    }

    const donationSelections = {
      candidates,
      total: donationAmountAdjusted.toString(),
    };

    // remove sessionStorage item if it was previously set and not removed
    clearDonationSelections();
    // convert donationSelection object to string so it can be stored in sessionStorage
    const donationSelectionsString = JSON.stringify(donationSelections);
    // save donationSelections to sessionStorage after user hits donate button on partner/issue/main page
    // and BEFORE user enters login/signup flow
    setDonationSelections(donationSelectionsString);

    handleNewDonationSelections();

    trackDonateAgainClicked({
      user_id: userId,
      amount: donationAmountAdjusted,
      query_string: issue.query_str,
    });
    return;
  };

  return (
    <DonateAgainCardContainer>
      <TopIssueCard issue={issue} fullHeight={true}>
        <>
          <Spacing $size={16} />
          <ButtonBrand
            onClick={handleDonate}
            to={getPathWithRef(`/checkout?p=${issue.query_str}&donate_again=true`)}
          >
            {`Express ${
              issue.query_str.startsWith('monthly') ? 'Monthly ' : ''
            }Donate ${formatNumberAsCurrency(donationAmountAdjusted)}`}
          </ButtonBrand>
          <Spacing $size={16} />
          <Line />
          <Eyebrow $color={theme.colors.inkBlue}>Recipients</Eyebrow>
          <Spacing $size={6} />
          <RecipientsContainer>
            {recipients.map(rec => (
              <Recipient key={rec.de_id}>
                <Body key={rec.de_id}>{rec.recipient_name}</Body>
              </Recipient>
            ))}
          </RecipientsContainer>
          <Spacing $size={25} />
          <DonateAgainCardLearnMore>
            <Link
              href={getPathWithRef(issue.learn_more_link || `/donate?p=${issue.query_str}`)}
              target="_blank"
            >
              <BodySmall>Learn More</BodySmall>
            </Link>
          </DonateAgainCardLearnMore>
        </>
      </TopIssueCard>
    </DonateAgainCardContainer>
  );
};
